import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import b from 'b_';
import { Editable, withReact, useSlate, Slate, ReactEditor } from 'slate-react';
import {
    Editor,
    Transforms,
    createEditor,
    Element as SlateElement,
} from 'slate';
import { withHistory } from 'slate-history';
import RawValueStore from '../../stores/helpers/RawValueStore';
import FuseButton from '../FuseControls/FuseButton';
import { insertImage, withImages } from './FuseWysiwyg/imgPlugin';
import { insertLink, withLinks } from './FuseWysiwyg/linkPlugin';
import { fixEditorValue } from './FuseWysiwyg/utils';
import { Leaf, Element } from './FuseWysiwyg/wysiwygRender';
import './NewFuseWysiwyg.css';

const block = b.lock('NewFuseWysiwyg');

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

const isMarkActive = (editor, format) => Editor.marks(editor)?.[format] === true;

const isBlockActive = (editor, format, blockType = 'type') => {
    const { selection } = editor;

    if (!selection) {
        return false;
    }

    const [match] = Array.from(Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
    }));

    return !!match;
};

const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type');
    const isList = LIST_TYPES.includes(format);

    Transforms.unwrapNodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n)
            && LIST_TYPES.includes(n.type) && !TEXT_ALIGN_TYPES.includes(format),
        split: true,
    });

    let newProperties;
    if (TEXT_ALIGN_TYPES.includes(format)) {
        newProperties = { align: isActive ? undefined : format };
    } else if (isActive) {
        newProperties = { type: 'paragraph' };
    } else if (isList) {
        newProperties = { type: 'list-item' };
    } else {
        newProperties = { type: format };
    }
    Transforms.setNodes(editor, newProperties);

    if (!isActive && isList) {
        Transforms.wrapNodes(editor, { type: format, children: [] });
    }
};

const toggleMark = (editor, format) => {
    if (isMarkActive(editor, format)) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

const ToolbarButton = ({ format, icon, type, title }) => {
    const editor = useSlate();
    const isActive = type === 'mark'
        ? isMarkActive(editor, format)
        : isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type');
    return <FuseButton
        type="quaternary"
        className={block('Button')}
        wrapperClassName={block('ButtonWrapper')}
        iconClassName={block('ButtonIcon')}
        icon={icon}
        active={isActive}
        qa={`wysiwyg-toolbar-button-${type}-${format}}`}
        title={title}
        onMouseDown={(event) => {
            event.preventDefault();

            if (format === 'link') {
                // eslint-disable-next-line no-alert
                const url = window.prompt('Enter the URL of the link:');
                if (!url) {
                    return;
                }
                insertLink(editor, url);
                return;
            }

            if (format === 'image') {
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');
                input.addEventListener('change', (fileEvent) => {
                    const file = fileEvent?.target?.files?.[0];
                    if (!file) {
                        return;
                    }
                    // TODO: check mime type
                    const reader = new window.FileReader();
                    const canvas = document.createElement('canvas');
                    const img = document.createElement('img');
                    reader.onload = (e) => {
                        img.onload = () => {
                            let ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);

                            let width = img.width;
                            let height = img.height;
                            if (height > 150) {
                                width *= 150 / height;
                                height = 150;
                            }
                            canvas.width = width;
                            canvas.height = height;
                            ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0, width, height);
                            insertImage(editor, canvas.toDataURL('image/png'), 'Logo');
                        };
                        img.src = e.target.result;
                    };
                    reader.readAsDataURL(file);
                });
                input.click();
                return;
            }

            if (type === 'mark') {
                toggleMark(editor, format);
            } else {
                toggleBlock(editor, format);
            }
        }}
    />;
};

ToolbarButton.propTypes = {
    format: PropTypes.string,
    icon: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
};


class NewFuseWysiwyg extends Component {
    static propTypes = {
        store: RawValueStore.PropTypes.isRequired,
        onFocus: PropTypes.func,
    };

    editor = withImages(withLinks(withHistory(withReact(createEditor()))));
    ref = React.createRef();

    renderElement = (props) => <Element {...props} root={this.ref.current} />;

    renderLeaf = (props) => <Leaf {...props} />;

    markButtons = [
        { format: 'bold', icon: 'format_bold', title: 'Bold' },
        { format: 'italic', icon: 'format_italic', title: 'Italic' },
        { format: 'underline', icon: 'format_underline', title: 'Underline' },
    ];

    blockButtons = [
        { format: 'heading-one', icon: 'looks_one', title: 'Heading 1' },
        { format: 'heading-two', icon: 'looks_two', title: 'Heading 2' },
        { format: 'numbered-list', icon: 'format_list_numbered', title: 'Numbered list' },
        { format: 'bulleted-list', icon: 'format_list_bulleted', title: 'Bulleted list' },
        { format: 'left', icon: 'format_align_left', title: 'Left align' },
        { format: 'center', icon: 'format_align_center', title: 'Center align' },
        { format: 'right', icon: 'format_align_right', title: 'Right align' },
        { format: 'justify', icon: 'format_align_justify', title: 'Justify' },
    ];

    focus = () => {
        if (!ReactEditor.isFocused(this.editor)) {
            ReactEditor.focus(this.editor);
            Transforms.select(this.editor, Editor.end(this.editor, []));
        }
    };

    render() {
        return <Slate
            editor={this.editor}
            value={fixEditorValue(this.props.store.getValue())}
            onChange={this.props.store.setValue}
        >
            <div className={block()} ref={this.ref}>
                <div className={block('Toolbar')}>
                    {this.markButtons.map((props) => <ToolbarButton key={props.format} {...props} type="mark" />)}
                    {this.blockButtons.map((props) => <ToolbarButton key={props.format} {...props} type="block" />)}
                    <ToolbarButton format="link" icon="link" titlle="Insert link" />
                    <ToolbarButton format="image" icon="image" title="Insert image" />
                </div>
                <div className={block('ContentWrapper')}>
                    <Editable
                        className={block('Content')}
                        renderElement={this.renderElement}
                        renderLeaf={this.renderLeaf}
                        spellCheck
                        onFocus={this.props.onFocus}
                    />
                </div>
            </div>
        </Slate>;
    }
}

export default observer(NewFuseWysiwyg);

